// FLEET LIVE's canvas primitives (#6778): the 2D-context surface the Command Deck and the
// Topology paint through, and the shapes both draw (prototype `:3344-3372`).
//
// `Paint2D` is the SUBSET of `CanvasRenderingContext2D` the painters call, so a test hands
// them a recorder (`fleet-live-canvas.test-support.ts`) and reads back what was drawn:
// happy-dom has no canvas, and `getContext('2d')` answers `null` there, which `fitCanvas`
// passes on so the caller paints nothing rather than throwing.

import { ellipsize } from '@developerz/kit';
import type { Particle } from './fleet-live-motion';

export type Paint2D = Pick<
  CanvasRenderingContext2D,
  | 'fillStyle'
  | 'strokeStyle'
  | 'lineWidth'
  | 'font'
  | 'textAlign'
  | 'textBaseline'
  | 'fillRect'
  | 'clearRect'
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'arc'
  | 'arcTo'
  | 'bezierCurveTo'
  | 'closePath'
  | 'fill'
  | 'stroke'
  | 'fillText'
  | 'measureText'
  | 'setLineDash'
  | 'createLinearGradient'
>;

/** The mono face every canvas label is set in: the design system's `--font-mono` stack. */
export const MONO = '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

/** The device pixel ratio a canvas is drawn at, capped at 2 (prototype `fit`, `:3357`). */
export function canvasRatio(dpr: number | undefined): number {
  return Math.min(2, dpr && dpr > 0 ? dpr : 1);
}

/**
 * Size `canvas` to `w` x `h` CSS pixels at the capped device ratio and hand back its
 * context with the ratio applied, or `null` when the browser gives no 2D context.
 */
export function fitCanvas(canvas: HTMLCanvasElement, w: number, h: number): Paint2D | null {
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;
  const dpr = canvasRatio(globalThis.devicePixelRatio);
  const pw = Math.round(w * dpr);
  const ph = Math.round(h * dpr);
  if (canvas.width !== pw) canvas.width = pw;
  if (canvas.height !== ph) canvas.height = ph;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/**
 * `color` at `a` times its own alpha. Reads the two notations the compiled tokens use (a
 * hex, and `rgba()` for the precomputed alpha tokens); anything else passes through.
 */
export function alpha(color: string, a: number): string {
  const c = color.trim();
  if (c.startsWith('#')) {
    const raw = c.slice(1);
    const hex =
      raw.length === 3
        ? raw
            .split('')
            .map((x) => x + x)
            .join('')
        : raw.slice(0, 6);
    const n = Number.parseInt(hex, 16);
    if (Number.isNaN(n)) return c;
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  }
  const m = /rgba?\(([^)]+)\)/.exec(c);
  if (m?.[1] === undefined) return c;
  const parts = m[1].split(/[\s,/]+/).filter((p) => p !== '');
  const base = parts.length >= 4 ? Number.parseFloat(parts[3] ?? '1') : 1;
  return `rgba(${parts[0]},${parts[1]},${parts[2]},${(base * a).toFixed(3)})`;
}

/** A rounded rectangle path. */
export function roundRect(
  ctx: Paint2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** A pointy-side hexagon path (prototype `hex`, `:4049`). */
export function hexPath(ctx: Paint2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i += 1) {
    const a = Math.PI / 6 + (i * Math.PI) / 3;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    else ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  ctx.closePath();
}

/** The four corner brackets around a blocked or parked pill (prototype `brackets`, `:3361`). */
export function brackets(ctx: Paint2D, x: number, y: number, w: number, h: number): void {
  const l = 5;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x, y + l);
  ctx.lineTo(x, y);
  ctx.lineTo(x + l, y);
  ctx.moveTo(x + w - l, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + l);
  ctx.moveTo(x + w, y + h - l);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w - l, y + h);
  ctx.moveTo(x + l, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + h - l);
  ctx.stroke();
  ctx.lineWidth = 1;
}

/** A name upper-cased and cut to `n` characters, "…" included (prototype `short`, `:3355`). */
export function cut(name: string, n: number): string {
  return ellipsize(name.toUpperCase(), { max: n });
}

/** The prototype's in-out cubic (`ease`, `:3353`). */
export function ease(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/** Draw a burst's sparks in `color`, each fading with its life. */
export function paintSparks(ctx: Paint2D, color: string, sparks: readonly Particle[]): void {
  for (const s of sparks) {
    ctx.fillStyle = alpha(color, s.life * 0.9);
    ctx.fillRect(s.x - s.s / 2, s.y - s.s / 2, s.s, s.s);
  }
}
